import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { InactivityManager } from "@/components/InactivityManager";
import { Toaster } from "@/components/ui/sonner";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import RequestDemoPage from "@/pages/RequestDemoPage";
import ActivatePage from "@/pages/ActivatePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminOverview from "@/pages/AdminOverview";
import AdminDemoRequests from "@/pages/AdminDemoRequests";
import AdminQuotes from "@/pages/AdminQuotes";
import AdminOrganizations from "@/pages/AdminOrganizations";
import AdminOrganizationWorkspace from "@/pages/AdminOrganizationWorkspace";
import AdminUsers from "@/pages/AdminUsers";
import AdminSolutions from "@/pages/AdminSolutions";
import AdminSolutionManage from "@/pages/AdminSolutionManage";
import AdminBilling from "@/pages/AdminBilling";
import AdminSettings from "@/pages/AdminSettings";
import PublicQuotePage from "@/pages/PublicQuotePage";
import PublicProposalPage from "@/pages/PublicProposalPage";
import LegacyFormRetiredPage from "@/pages/public/LegacyFormRetiredPage";
import PublicIntakePage from "@/pages/PublicIntakePage";
import OrgOwnerLayout from "@/components/org/OrgOwnerLayout";
import OrgOverview from "@/pages/org/OrgOverview";
import OrgTeamAccess from "@/pages/org/OrgTeamAccess";
import OrgSolutionShell from "@/pages/org/OrgSolutionShell";
import OrgServices from "@/pages/org/OrgServices";
import OrgProducts from "@/pages/org/OrgProducts";
import OrgBundles from "@/pages/org/OrgBundles";
import OrgCredentials from "@/pages/org/OrgCredentials";
import OrgConnections from "@/pages/org/OrgConnections";
import OrgCustomerIntake from "@/pages/org/OrgCustomerIntake";
import IntakeModelWizardPage from "@/pages/org/IntakeModelWizardPage";
import OrgFinanceEstimatesProposals from "@/pages/org/OrgFinanceEstimatesProposals";
import OrgFinancePaymentReminders from "@/pages/org/OrgFinancePaymentReminders";
import OrgServiceAreas from "@/pages/org/OrgServiceAreas";
import OrgPlaceholder from "@/pages/org/OrgPlaceholder";
import OrgAnalytics from "@/pages/org/OrgAnalytics";
import OrgSettings from "@/pages/org/OrgSettings";
import OrgBilling from "@/pages/org/OrgBilling";
import { useLenis } from "@/hooks/useLenis";
import { Navbar } from "@/components/landing/Navbar";
import { Hero } from "@/components/landing/Hero";
import { Marquee } from "@/components/landing/Marquee";
import { Operations } from "@/components/landing/Operations";
import { ConnectOrNova } from "@/components/landing/ConnectOrNova";
import { HowItWorks } from "@/components/landing/HowItWorks";
import { NovaAssistant } from "@/components/landing/NovaAssistant";
import { Integrations } from "@/components/landing/Integrations";
import { Pricing } from "@/components/landing/Pricing";
import { AboutNova } from "@/components/landing/AboutNova";
import { Footer } from "@/components/landing/Footer";

const LandingPage = () => {
  useLenis();
  return (
    <div id="top" className="bg-white text-nova-navy font-sans antialiased">
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Operations />
        <ConnectOrNova />
        <HowItWorks />
        <NovaAssistant />
        <Integrations />
        <Pricing />
        <AboutNova />
      </main>
      <Footer />
    </div>
  );
};

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Session exchange must run before any route/auth checks
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/request-demo" element={<RequestDemoPage />} />
      <Route path="/request-access" element={<Navigate to="/request-demo" replace />} />
      <Route path="/signup" element={<Navigate to="/request-demo" replace />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/activate/:token" element={<ActivatePage />} />
      <Route path="/quote/:token" element={<PublicQuotePage />} />
      <Route path="/proposal/:token" element={<PublicProposalPage />} />
      <Route path="/forms/:publicId" element={<LegacyFormRetiredPage />} />
      <Route path="/intake/:code" element={<PublicIntakePage />} />
      <Route
        path="/admin/access-requests"
        element={<Navigate to="/admin/demo-requests" replace />}
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute requirePlatformAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<AdminOverview />} />
        <Route path="demo-requests" element={<AdminDemoRequests />} />
        <Route path="quotes" element={<AdminQuotes />} />
        <Route path="organizations" element={<AdminOrganizations />} />
        <Route path="organizations/:orgId" element={<AdminOrganizationWorkspace />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="solutions" element={<AdminSolutions />} />
        <Route path="solutions/:solutionId" element={<AdminSolutionManage />} />
        <Route path="billing" element={<AdminBilling />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireOnboarded={false}>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <OrgOwnerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OrgOverview />} />
        <Route path="analytics" element={<OrgAnalytics />} />
        <Route path="team" element={<OrgTeamAccess />} />
        <Route path="service-areas" element={<OrgServiceAreas />} />
        <Route path="solutions/:slug" element={<OrgSolutionShell />} />
        <Route path="services" element={<OrgServices />} />
        <Route path="products" element={<OrgProducts />} />
        <Route path="bundles" element={<OrgBundles />} />
        <Route path="credentials" element={<OrgCredentials />} />
        <Route path="connections" element={<OrgConnections />} />
        <Route path="connections/customer-intake" element={<OrgCustomerIntake />} />
        <Route path="connections/customer-intake/models/:modelId" element={<IntakeModelWizardPage />} />
        <Route path="finance/estimates-proposals" element={<OrgFinanceEstimatesProposals />} />
        <Route path="finance/payment-reminders" element={<OrgFinancePaymentReminders />} />
        <Route path="workflows" element={<OrgPlaceholder title="Automations" />} />
        <Route path="settings" element={<OrgSettings />} />
        <Route path="billing" element={<OrgBilling />} />
      </Route>
      <Route path="*" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App bg-white text-nova-navy font-sans antialiased">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <InactivityManager />
            <Toaster position="top-right" />
            <AppRouter />
          </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
